import { useState } from "react";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import PageSEO from "../../components/feature/PageSEO";
import { Link } from "react-router-dom";
import { useScrollAnimation } from "../../hooks/useScrollAnimation";

const categories = ["All", "Web Design", "Redesign", "SEO", "E-commerce"];

const caseStudies = [
  {
    title: "Bright Smiles Dental Practice",
    category: "Web Design",
    industry: "Healthcare",
    challenge: "No website, losing patients to competitors who had a strong online presence.",
    solution: "Built a fresh, trust-focused website with appointment booking, service pages, and local SEO.",
    results: ["+410% organic traffic", "40+ new patients/month", "Page 1 Google ranking"],
    img: "https://public.readdy.ai/ai/img_res/ae8609901487743230000f17c2b45f9f.jpg",
    color: "bg-teal-50",
    accent: "text-teal-700 bg-teal-100",
  },
  {
    title: "Ironwood Builders & Contractors",
    category: "Redesign",
    industry: "Construction",
    challenge: "Outdated 2012 website was losing quotes to more professional-looking competitors.",
    solution: "Complete website redesign with project gallery, testimonials, and lead generation forms.",
    results: ["3x more quote requests", "+185% mobile traffic", "Avg. job value increased 35%"],
    img: "https://readdy.ai/api/search-image?query=construction%20company%20website%20on%20laptop%20mockup%2C%20bold%20dark%20professional%20design%2C%20project%20gallery%20layout%2C%20modern%20contractor%20website%2C%20industrial%20office%20setting%2C%20warm%20wooden%20desk%2C%20dramatic%20lighting&width=700&height=480&seq=case-builders-001&orientation=landscape",
    color: "bg-stone-50",
    accent: "text-stone-700 bg-stone-200",
  },
  {
    title: "The Bloom Studio — Florist",
    category: "E-commerce",
    industry: "Retail",
    challenge: "Taking orders by phone only — missing out on online sales and a younger audience.",
    solution: "E-commerce website with online ordering, seasonal collections, and Google Shopping integration.",
    results: ["+220% online revenue", "68% of orders now online", "Top 3 Google Maps local"],
    img: "https://readdy.ai/api/search-image?query=beautiful%20florist%20e-commerce%20website%20on%20tablet%20and%20desktop%2C%20elegant%20pink%20and%20white%20design%2C%20product%20grid%20layout%2C%20fresh%20flowers%20beside%20devices%2C%20airy%20studio%20setting%2C%20soft%20natural%20window%20light&width=700&height=480&seq=case-florist-001&orientation=landscape",
    color: "bg-rose-50",
    accent: "text-rose-700 bg-rose-100",
  },
  {
    title: "Tanner&apos;s Auto Repair",
    category: "SEO",
    industry: "Automotive",
    challenge: "Not appearing anywhere on Google despite being in business for 18 years.",
    solution: "Local SEO strategy: Google Business Profile, on-page optimisation, and targeted content.",
    results: ["First on Google Maps", "+280% phone calls", "6-week turnaround"],
    img: "https://readdy.ai/api/search-image?query=auto%20repair%20shop%20website%20on%20laptop%20screen%2C%20clean%20professional%20design%2C%20service%20menu%20layout%2C%20garage%20background%20blurred%2C%20warm%20industrial%20lighting%2C%20mechanic%20tools%20on%20desk%20beside%20laptop&width=700&height=480&seq=case-auto-001&orientation=landscape",
    color: "bg-zinc-50",
    accent: "text-zinc-700 bg-zinc-200",
  },
  {
    title: "Webb&apos;s Landscaping & Gardens",
    category: "Redesign",
    industry: "Trades",
    challenge: "Existing site had no photos, no testimonials, and no contact form — zero leads online.",
    solution: "Redesigned with a stunning gallery, video case studies, testimonials, and a quote request form.",
    results: ["+180% quote requests", "Featured in local press", "Avg. project value +£1,200"],
    img: "https://readdy.ai/api/search-image?query=landscaping%20company%20website%20on%20laptop%20mockup%2C%20lush%20green%20garden%20photography%20featured%2C%20clean%20elegant%20design%2C%20nature%20themed%20layout%2C%20outdoor%20garden%20table%20setting%2C%20warm%20afternoon%20sun&width=700&height=480&seq=case-landscaping-001&orientation=landscape",
    color: "bg-emerald-50",
    accent: "text-emerald-700 bg-emerald-100",
  },
  {
    title: "Sunrise Yoga Studio",
    category: "Web Design",
    industry: "Wellness",
    challenge: "No digital presence — relying entirely on word of mouth to grow class bookings.",
    solution: "Brand new website with class schedule, online booking, instructor profiles, and blog.",
    results: ["+320% class bookings", "New members from Google", "45% bounce rate reduction"],
    img: "https://readdy.ai/api/search-image?query=yoga%20studio%20website%20on%20laptop%20and%20mobile%20phone%2C%20serene%20minimal%20design%2C%20warm%20amber%20and%20white%20colour%20palette%2C%20class%20schedule%20layout%2C%20peaceful%20wellness%20studio%20setting%2C%20soft%20golden%20hour%20lighting&width=700&height=480&seq=case-yoga-001&orientation=landscape",
    color: "bg-amber-50",
    accent: "text-amber-700 bg-amber-100",
  },
];

function PortfolioHero() {
  const { ref, visible } = useScrollAnimation(0.05);
  return (
    <section className="pt-32 pb-16 bg-white" ref={ref}>
      <div className="container-md">
        <nav className="text-sm text-zinc-400 mb-6 flex items-center gap-2">
          <Link to="/" className="hover:text-accent cursor-pointer">Home</Link>
          <i className="ri-arrow-right-s-line" />
          <span className="text-zinc-700">Portfolio</span>
        </nav>
        <div
          className={`transition-all duration-700 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
          }`}
        >
          <span className="text-accent text-xs font-semibold uppercase tracking-[0.2em] mb-4 block">
            Our work
          </span>
          <h1 className="font-heading text-4xl md:text-6xl font-bold text-zinc-900 leading-tight mb-5">
            Case Studies &amp; Portfolio
          </h1>
          <p className="text-zinc-600 text-lg max-w-2xl leading-relaxed">
            Real results for real small businesses. Here&apos;s how we&apos;ve helped business owners get more customers, rank higher on Google, and grow their <strong>digital presence</strong>.
          </p>
        </div>

        {/* Stats row */}
        <div
          className={`grid grid-cols-2 md:grid-cols-4 gap-5 mt-12 transition-all duration-700 delay-200 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
          }`}
        >
          {[
            { val: "100+", label: "Websites Delivered" },
            { val: "247%", label: "Avg. Lead Increase" },
            { val: "94%", label: "Client Retention" },
            { val: "14 days", label: "Avg. Build Time" },
          ].map((s) => (
            <div key={s.label} className="bg-stone-50 rounded-xl p-5 text-center">
              <p className="font-heading text-2xl md:text-3xl font-bold text-zinc-900">{s.val}</p>
              <p className="text-zinc-500 text-xs mt-1">{s.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CaseStudyCard({ study, delay }: { study: typeof caseStudies[0]; delay: number }) {
  const { ref, visible } = useScrollAnimation();

  return (
    <div
      ref={ref}
      className={`rounded-2xl overflow-hidden border border-zinc-100 transition-all duration-500 hover:-translate-y-1 group ${
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
      }`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {/* Image */}
      <div className="w-full h-56 overflow-hidden bg-zinc-100">
        <img
          src={study.img}
          alt={`${study.title} website case study`}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
      </div>

      {/* Content */}
      <div className={`p-6 ${study.color}`}>
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <span className={`text-xs font-semibold px-3 py-1 rounded-full ${study.accent}`}>
            {study.category}
          </span>
          <span className="text-zinc-500 text-xs">{study.industry}</span>
        </div>

        <h3
          className="font-heading text-xl font-bold text-zinc-900 mb-3"
          dangerouslySetInnerHTML={{ __html: study.title }}
        />

        <div className="mb-4 space-y-2">
          <div>
            <span className="text-xs font-semibold text-zinc-500 uppercase tracking-wider">Challenge</span>
            <p className="text-zinc-600 text-sm mt-1">{study.challenge}</p>
          </div>
          <div>
            <span className="text-xs font-semibold text-zinc-500 uppercase tracking-wider">Solution</span>
            <p className="text-zinc-600 text-sm mt-1">{study.solution}</p>
          </div>
        </div>

        {/* Results */}
        <div className="flex flex-wrap gap-2">
          {study.results.map((r) => (
            <span
              key={r}
              className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-full"
            >
              <i className="ri-arrow-up-line text-xs" />
              {r}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

const siteUrl = (import.meta.env.VITE_SITE_URL ?? '').replace(/\/$/, '');

const portfolioSchemas = [
  {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Portfolio & Case Studies | Small Business Website Design Results",
    "description": "Real results for real small businesses. See how our website design and SEO services have helped businesses get more customers and rank higher on Google.",
    "url": `${siteUrl || "https://example.com"}/portfolio`,
    "inLanguage": "en-GB",
    "breadcrumb": {
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": siteUrl || "https://example.com"
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Portfolio",
          "item": `${siteUrl || "https://example.com"}/portfolio`
        }
      ]
    }
  },
  {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Small Business Website Case Studies",
    "description": "Case studies showcasing the results of our small business website design and SEO services.",
    "url": `${siteUrl || "https://example.com"}/portfolio`,
    "numberOfItems": 6,
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Bright Smiles Dental Practice",
        "description": "Built a trust-focused website with appointment booking and local SEO. Result: +410% organic traffic, 40+ new patients/month."
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Ironwood Builders & Contractors",
        "description": "Complete website redesign with project gallery and lead generation forms. Result: 3x more quote requests."
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": "The Bloom Studio — Florist",
        "description": "E-commerce website with online ordering and Google Shopping integration. Result: +220% online revenue."
      },
      {
        "@type": "ListItem",
        "position": 4,
        "name": "Tanner's Auto Repair",
        "description": "Local SEO strategy with Google Business Profile and on-page optimisation. Result: First on Google Maps, +280% phone calls."
      },
      {
        "@type": "ListItem",
        "position": 5,
        "name": "Webb's Landscaping & Gardens",
        "description": "Redesigned with gallery, testimonials, and quote request form. Result: +180% quote requests."
      },
      {
        "@type": "ListItem",
        "position": 6,
        "name": "Sunrise Yoga Studio",
        "description": "Brand new website with class schedule and online booking. Result: +320% class bookings."
      }
    ]
  }
];

export default function PortfolioPage() {
  const [activeCategory, setActiveCategory] = useState("All");

  const filtered =
    activeCategory === "All"
      ? caseStudies
      : caseStudies.filter((s) => s.category === activeCategory);

  const { ref, visible } = useScrollAnimation(0.05);

  return (
    <main>
      <PageSEO
        title="Portfolio & Case Studies | Small Business Website Results | PixelCraft Agency"
        description="Real results for real small businesses. See how our web design and SEO services helped businesses get more customers, rank higher on Google, and grow their digital presence."
        canonical="/portfolio"
        schema={portfolioSchemas}
      />
      <Navbar />
      <PortfolioHero />

      {/* Filter + Grid */}
      <section className="section-pad bg-white">
        <div className="container-md">
          {/* Filter bar */}
          <div
            ref={ref}
            className={`flex flex-wrap gap-2 mb-10 transition-all duration-700 ${
              visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            }`}
          >
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer ${
                  activeCategory === cat
                    ? "bg-accent text-white"
                    : "bg-stone-100 text-zinc-600 hover:bg-stone-200"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((study, i) => (
              <CaseStudyCard key={study.title} study={study} delay={i * 100} />
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="section-pad bg-stone-50">
        <div className="container-md text-center">
          <span className="text-accent text-xs font-semibold uppercase tracking-[0.2em] mb-4 block">
            Your business could be next
          </span>
          <h2 className="font-heading text-3xl md:text-5xl font-bold text-zinc-900 mb-5">
            Ready to get results like these?
          </h2>
          <p className="text-zinc-600 text-base max-w-xl mx-auto mb-8">
            Every business on this page started exactly where you are. Let&apos;s build something that works as hard as you do.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="whitespace-nowrap inline-flex items-center justify-center gap-2 bg-accent hover:bg-accent-dark text-white font-semibold px-8 py-4 rounded-lg text-base transition-all cursor-pointer"
            >
              Start Your Project
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-arrow-right-line" />
              </span>
            </Link>
            <Link
              to="/services"
              className="whitespace-nowrap inline-flex items-center justify-center gap-2 border border-zinc-300 hover:border-accent text-zinc-700 hover:text-accent font-semibold px-8 py-4 rounded-lg text-base transition-all cursor-pointer"
            >
              View Our Services
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
